import React from 'react';
import { Selection } from 'src/domains/sportsbook/webview/components/Selection';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MarketHeader } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { observer } from 'src/utils/mobx-react';
import { MarketId, SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import {
    HeaderCell,
    LineCell,
    MarketRows,
    SelectionCell,
    TotalGoalsRoot,
    SelectionHeader,
} from './TotalGoalsMarketGroup.style';
import { SelectionsGroupWrap } from 'src/domains/sportsbook/webview/components/selectionsGroupUnified/SelectionGroupUnified.style';

interface PropsType {
    templateName: string;
    marketId: MarketId;
    markets: Array<MarketId>;
    selections: Record<number | string, SelectionModel[]>;
    isOpen: boolean;
    onClick(): void;
}

const INITIAL_ROWS_LIMIT = 4;

interface TotalGoalsRowType {
    key: string;
    line: number | null;
    overSelectionId: SelectionId | undefined;
    underSelectionId: SelectionId | undefined;
}

const OVER_SELECTION_NAMES = ['over'];
const UNDER_SELECTION_NAMES = ['under'];
const OVER_SELECTION_TEMPLATE_IDS = ['o', 'ov', 'over'];
const UNDER_SELECTION_TEMPLATE_IDS = ['u', 'un', 'under'];

const getSelectionWeight = (selection: SelectionModel, templateIds: Array<string>, names: Array<string>): number => {
    const templateId = selection.templateId?.toLowerCase().trim() ?? '';
    const name = selection.name.toLowerCase().trim();

    if (templateIds.includes(templateId)) {
        return 3;
    }
    if (name === names[0]) {
        return 2;
    }
    if (names.some((item) => name.includes(item))) {
        return 1;
    }
    return 0;
};

const getBestMatchingSelection = (
    marketSelections: Array<SelectionModel>,
    templateIds: Array<string>,
    names: Array<string>
): SelectionModel | undefined => {
    return marketSelections.reduce<SelectionModel | undefined>((best, selection) => {
        const selectionWeight = getSelectionWeight(selection, templateIds, names);
        if (selectionWeight === 0) {
            return best;
        }
        if (best === undefined) {
            return selection;
        }
        const bestWeight = getSelectionWeight(best, templateIds, names);
        if (selectionWeight > bestWeight) {
            return selection;
        }
        return best;
    }, undefined);
};

const getMarketRows = (
    markets: Array<MarketId>,
    selections: Record<number | string, SelectionModel[]>
): TotalGoalsRowType[] => {
    return markets
        .map((marketId): TotalGoalsRowType | null => {
            const marketModel = marketId.getModel();
            if (marketModel === null) {
                return null;
            }

            const marketSelections = selections[marketModel.id] ?? [];
            const overSelection = getBestMatchingSelection(
                marketSelections,
                OVER_SELECTION_TEMPLATE_IDS,
                OVER_SELECTION_NAMES
            );
            const underSelection = getBestMatchingSelection(
                marketSelections,
                UNDER_SELECTION_TEMPLATE_IDS,
                UNDER_SELECTION_NAMES
            );

            const marketLineByCountry = marketModel.name.match(/\d.+/);

            let line = null;
            if (marketModel.line) {
                line = marketModel.line;
            } else if (marketLineByCountry) {
                line = parseFloat(marketLineByCountry[0]);
            }

            return {
                key: `${marketModel.id}-${line ?? 'line-null'}`,
                line,
                overSelectionId: overSelection?.id2,
                underSelectionId: underSelection?.id2,
            };
        })
        .filter((row): row is TotalGoalsRowType => row !== null)
        .sort((a, b) => {
            if (a.line === null && b.line === null) {
                return 0;
            }
            if (a.line === null) {
                return 1;
            }
            if (b.line === null) {
                return -1;
            }
            return a.line - b.line;
        });
};

const lineLabel = (line: number | null): string => {
    if (line === null) {
        return '-';
    }
    return Number.isInteger(line) ? `${line}.0` : `${line}`;
};

export const TotalGoalsMarketGroup = observer('TotalGoalsMarketGroup', (props: PropsType) => {
    const { marketId, markets, selections, isOpen, onClick, templateName } = props;

    return (
        <TotalGoalsRoot className='market-group--total-goals'>
            <MarketHeader
                name={templateName}
                isOpen={isOpen}
                onClick={onClick}
                marketId={marketId}
            />
            {isOpen ? (
                <MarketBody
                    markets={markets}
                    selections={selections}
                />
            ) : null}
        </TotalGoalsRoot>
    );
});

const MarketBody = observer(
    'MarketBody',
    (props: { markets: Array<MarketId>; selections: Record<number | string, SelectionModel[]> }) => {
        const { markets, selections } = props;
        const [isExpanded, setIsExpanded] = React.useState(false);
        const rows = getMarketRows(markets, selections);
        const firstMarketModel = markets[0]?.getModel() ?? null;
        const visibleRows = isExpanded === true ? rows : rows.slice(0, INITIAL_ROWS_LIMIT);

        const toggleExpand = (e: React.MouseEvent): void => {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
        };

        if (firstMarketModel === null) {
            return null;
        }
        return (
            <>
                <SelectionHeader>
                    <HeaderCell>
                        <I18n
                            langKey='events.total-goals-market.goals'
                            defaultText='Goals'
                        />
                    </HeaderCell>
                    <HeaderCell>
                        <I18n
                            langKey='events.total-goals-market.over'
                            defaultText='Over'
                        />
                    </HeaderCell>
                    <HeaderCell>
                        <I18n
                            langKey='events.total-goals-market.under'
                            defaultText='Under'
                        />
                    </HeaderCell>
                </SelectionHeader>
                <SelectionsGroupWrap>
                    <MarketRows>
                        {visibleRows.map((row) => {
                            return (
                                <React.Fragment key={row.key}>
                                    <LineCell>{lineLabel(row.line)}</LineCell>
                                    <SelectionCell>
                                        <Selection selectionId={row.overSelectionId} />
                                    </SelectionCell>
                                    <SelectionCell>
                                        <Selection selectionId={row.underSelectionId} />
                                    </SelectionCell>
                                </React.Fragment>
                            );
                        })}
                    </MarketRows>
                    {rows.length > INITIAL_ROWS_LIMIT ? (
                        <ExpandButton
                            isExpanded={isExpanded}
                            toggleExpand={toggleExpand}
                        />
                    ) : null}
                </SelectionsGroupWrap>
            </>
        );
    }
);
